import { isBootstrapWorkPending } from "@/lib/wizardStatus";
import { WIZARD_STEPS, wizardLocationPath, wizardRoutePath, type StepDefinition } from "@/lib/steps";
import type { BridgeStatus, StepKey, WizardPreview, WizardState } from "@/types/wizard";

export type WizardScenario = "first-install" | "config-update" | "ready";

export interface WizardFlowInput {
  state: WizardState | undefined;
  status: BridgeStatus | undefined;
  previewResult: WizardPreview | null;
  applyResult: WizardPreview | null;
  pathname: string;
}

export interface StepFlowState {
  visible: boolean;
  accessible: boolean;
  completed: boolean;
}

export interface WizardFlowSnapshot {
  scenario: WizardScenario;
  requiresAction: boolean;
  steps: Record<StepKey, StepFlowState>;
  visibleSteps: StepDefinition[];
  recommendedPath: string;
  currentStep: StepKey | null;
}

const LEGACY_SEGMENTS = new Set(["preview", "apply", "prerequisites", "overview", "welcome"]);
const LEGACY_REDIRECT_SEGMENTS = new Set(["prerequisites", "overview", "welcome"]);

export function resolveScenario(state: WizardState | undefined): WizardScenario {
  if (state?.scenario === "first-install" || state?.scenario === "config-update" || state?.scenario === "ready") {
    return state.scenario;
  }

  if (state?.firstStartPending) {
    return "first-install";
  }

  if (state?.updatePending) {
    return "config-update";
  }

  return "ready";
}

export function normalizeWizardSegment(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const last = segments[segments.length - 1] ?? "review";
  if (LEGACY_SEGMENTS.has(last)) {
    return "review";
  }

  return last;
}

export function stepKeyFromPathname(pathname: string): StepKey | null {
  const segment = normalizeWizardSegment(pathname);
  const step = WIZARD_STEPS.find((item) => item.segment === segment);
  return step?.key ?? null;
}

export function computeWizardFlow(input: WizardFlowInput): WizardFlowSnapshot {
  const requiresAction = isBootstrapWorkPending(input.state, input.status);
  const scenario = resolveScenario(input.state);
  const hasApply = !!input.applyResult;
  const returnedFromFinalize = input.pathname.includes("finalized=1");
  const canAccessDone = hasApply || returnedFromFinalize;

  const review: StepFlowState = {
    visible: true,
    accessible: true,
    completed: hasApply,
  };

  const secrets: StepFlowState = {
    visible: hasApply,
    accessible: hasApply,
    completed: false,
  };

  const done: StepFlowState = {
    visible: canAccessDone,
    accessible: canAccessDone,
    completed: returnedFromFinalize,
  };

  const steps: Record<StepKey, StepFlowState> = {
    review,
    secrets,
    done,
  };

  const visibleSteps = WIZARD_STEPS.filter((step) => steps[step.key].visible);

  return {
    scenario,
    requiresAction,
    steps,
    visibleSteps,
    recommendedPath: getRecommendedPath(),
    currentStep: stepKeyFromPathname(input.pathname),
  };
}

export function getRecommendedPath(): string {
  return wizardRoutePath("review");
}

export function getBlockedRedirect(pathname: string, flow: WizardFlowSnapshot): string | null {
  const rawSegment = pathname.split("/").filter(Boolean).pop() ?? "";
  if (LEGACY_REDIRECT_SEGMENTS.has(rawSegment)) {
    return wizardRoutePath("review");
  }

  const segment = normalizeWizardSegment(pathname);
  const step = WIZARD_STEPS.find((item) => item.segment === segment);
  if (!step) {
    return null;
  }

  const state = flow.steps[step.key];
  if (state.accessible) {
    return LEGACY_SEGMENTS.has(rawSegment) ? wizardRoutePath("review") : null;
  }

  const firstBlockedIndex = WIZARD_STEPS.findIndex(
    (item) => flow.steps[item.key].visible && !flow.steps[item.key].accessible,
  );

  if (firstBlockedIndex >= 0) {
    return wizardRoutePath(WIZARD_STEPS[firstBlockedIndex].segment);
  }

  const lastAccessible = [...WIZARD_STEPS]
    .reverse()
    .find((item) => flow.steps[item.key].visible && flow.steps[item.key].accessible);

  return lastAccessible ? wizardRoutePath(lastAccessible.segment) : wizardRoutePath("review");
}

export function isWizardPath(pathname: string): boolean {
  const base = wizardLocationPath("").replace(/\/$/, "");
  return pathname === base || pathname.startsWith(`${base}/`);
}
